import moment, { Moment } from "./SensolusMoment";
// Window.LANG type is defined in src/types/global.d.ts

// e.g. 2026-09-23T04:17:00Z, 2026-09-23T04:17:00+0000, 2026-09-23T04:17:00.123+07:00
const ISO_WITH_OFFSET = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/;

export interface SensolusDatetimeReactType {
  FORMAT_SERVER: string;
  FORMAT_CLIENT: string;
  ISO_8601_PATTERN: string;
  fromNow: (dateTime?: Moment | string) => string;
  primitiveFromNow: (dateTime?: Moment | string) => string;
  parse: (str: string | Moment, selection?: "client" | "server") => Moment;
  format: (datetime?: Moment | string, selection?: "client" | "server") => string;
  formatServer: (datetime?: Moment | string) => string;
  formatDuration: (duration: number) => string;
  formatDetailedDuration: (duration: number) => string;
}

export const SensolusDatetimeReact: SensolusDatetimeReactType = {
  FORMAT_SERVER: "YYYY-MM-DD HH:mm:ss ZZ",
  FORMAT_CLIENT: "YYYY-MM-DD HH:mm:ss",
  ISO_8601_PATTERN: "YYYY-MM-DDTHH:mm:ssZZ",

  fromNow: function (dateTime?: Moment | string): string {
    // dateTime should comes from SensolusMoment with correct timezone and locale
    let dt: Moment;
    if (!dateTime) {
      dt = moment();
    } else if (!moment.isMoment(dateTime)) {
      dt = moment(dateTime);
    } else {
      dt = dateTime;
    }

    const duration = moment.duration(moment().diff(dt));
    const minutes = duration.asMinutes();

    if (minutes < 60) {
      return dt.fromNow();
    }
    return dt.format("LLL");
  },

  primitiveFromNow: function (dateTime?: Moment | string): string {
    // dateTime should comes from SensolusMoment with correct timezone and locale
    let dt: Moment;
    if (!dateTime) {
      dt = moment();
    } else if (!moment.isMoment(dateTime)) {
      dt = moment(dateTime);
    } else {
      dt = dateTime;
    }

    return dt.fromNow();
  },

  parse: function (str: string | Moment, selection?: "client" | "server"): Moment {
    // If already a Moment, return it directly
    if (moment.isMoment(str)) {
      return str;
    }
    if (!selection || selection === "client") {
      // An ISO string with an explicit offset is an absolute instant: keep the
      // offset so it renders in the user's timezone. FORMAT_CLIENT would drop it.
      return ISO_WITH_OFFSET.test(str) ? moment(str, moment.ISO_8601) : moment(str, this.FORMAT_CLIENT);
    }
    return moment(str, this.FORMAT_SERVER);
  },

  format: function (datetime?: Moment | string, selection?: "client" | "server"): string {
    let dt: Moment;
    if (!datetime) {
      dt = moment();
    } else if (!moment.isMoment(datetime)) {
      dt = moment(datetime);
    } else {
      dt = datetime;
    }

    return !selection || selection === "client"
      ? dt.format(this.FORMAT_CLIENT)
      : dt.format(this.FORMAT_SERVER);
  },

  formatServer: function (datetime?: Moment | string): string {
    let dt: Moment;
    if (!datetime) {
      dt = moment();
    } else if (!moment.isMoment(datetime)) {
      dt = moment(datetime);
    } else {
      dt = datetime;
    }
    return dt.format(this.FORMAT_SERVER);
  },

  formatDuration: function (_duration: number): string {
    const duration = moment.duration(_duration),
      _day = Math.floor(duration.asDays()),
      _hour = duration.hours(),
      _min = duration.minutes();

    let result = "";
    const d_key = window.LANG.d_key + " ",
      h_key = window.LANG.h_key + " ",
      m_key = "m";
    if (_day) {
      result += _day + d_key;
    }
    if (_hour) {
      result += _hour + h_key;
    }
    if (_min) {
      result += _min + m_key;
    }
    if (!result) {
      result = "0";
    }
    return result;
  },

  formatDetailedDuration: function (_duration: number): string {
    const duration = moment.duration(_duration),
      _day = Math.floor(duration.asDays()),
      _hour = duration.hours(),
      _min = duration.minutes(),
      _sec = duration.seconds();

    let result = "";
    const d_key = window.LANG.d_key + " ",
      h_key = window.LANG.h_key + " ",
      m_key = "m ",
      s_key = "s";
    if (_day) {
      result += _day + d_key;
    }
    if (_hour) {
      result += _hour + h_key;
    }
    if (_min) {
      result += _min + m_key;
    }
    if (_sec) {
      result += _sec + s_key;
    }
    if (!result) {
      result = "0";
    }
    return result;
  },
};

export interface Config {
  API_URL: string;
  MODE: string;
}

const config: Config = {
  API_URL: import.meta.env.VITE_REACT_APP_API_URL || "",
  MODE: import.meta.env.MODE,
};

export default config;
